import { useEffect, useRef } from 'react';

import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { GenUiRenderProps } from '../../shared/ui/registry.js';
import type {
  ConsultPlacesBlock,
  PlaceDetails,
  PlacesPort,
} from '@voll-intelligence/genui-contract';
import { PlacesGoogleMap } from './google_map.js';
import { MapDialog } from './map_dialog.js';
import { PlaceSheet } from './place_sheet.js';
import { SummaryCard } from './place_summary.js';
import { useConsultPlaces } from './use_consult_places.js';

/** Encosta o card em destaque na calha do trilho — a mesma posição em que o
 *  `scroll-snap-align: start` o deixaria (padrão de `hotel_results.tsx`). */
function useDockFocused(rail: React.RefObject<HTMLUListElement | null>, focusedId?: string) {
  useEffect(() => {
    const list = rail.current;
    if (!list || !focusedId) return;
    const item = Array.from(list.children).find(
      (child) => child instanceof HTMLElement && child.dataset.placeId === focusedId,
    );
    if (!(item instanceof HTMLElement) || typeof list.scrollTo !== 'function') return;
    const gutter = Number.parseFloat(getComputedStyle(list).paddingLeft) || 0;
    list.scrollTo({ left: item.offsetLeft - list.offsetLeft - gutter, behavior: 'smooth' });
  }, [rail, focusedId]);
}

type PlaceState = ReturnType<typeof useConsultPlaces>['states'][string];

/** O título do bloco quando o contexto pede (o local de um evento), senão o padrão. */
function PlacesHeader({ title }: { readonly title?: string }) {
  const { t } = useGenUiI18n();
  return (
    <header>
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">VOLL</p>
      <h2 className="text-lg font-semibold">{title ?? t('consultPlaces.title')}</h2>
    </header>
  );
}

/** Um lugar só não é trilho: o card ocupa a largura toda. */
function railItemClass(single: boolean): string {
  return single
    ? 'flex w-full shrink-0'
    : 'flex w-[80%] shrink-0 snap-start sm:w-[calc(50%-0.375rem)]';
}

/** Um item do trilho em qualquer estado: esqueleto, erro com retry ou o card. */
function PlaceTile({
  id,
  state,
  position,
  port,
  selected,
  onSelect,
  onRetry,
  wide = false,
}: {
  readonly id: string;
  readonly state: PlaceState | undefined;
  readonly position: number;
  readonly port?: PlacesPort;
  readonly selected: boolean;
  readonly onSelect: (placeId: string, opener: HTMLButtonElement) => void;
  readonly onRetry: (placeId: string) => void;
  readonly wide?: boolean;
}) {
  const { t } = useGenUiI18n();
  if (!state || state.status === 'loading')
    return (
      <div role="status" className="h-28 w-full animate-pulse rounded-2xl bg-muted">
        <span className="sr-only">{t('consultPlaces.loadingPlace')}</span>
      </div>
    );
  // Em erro o card não tem o NOME (o resumo é o que falhou, e o bloco só carrega
  // ids — conteúdo do Google não persiste no wire), então identifica o lugar pela
  // POSIÇÃO: o mesmo número do pin no mapa e da ordem em que a prosa o cita (#3249).
  if (state.status === 'error')
    return (
      <div role="alert" className="flex w-full gap-3 rounded-2xl border border-border p-4">
        <span
          aria-hidden
          className="grid size-7 shrink-0 place-items-center rounded-full bg-muted text-sm font-semibold text-muted-foreground"
        >
          {position}
        </span>
        <div className="min-w-0 flex-1">
          <p>{t('consultPlaces.placeErrorAt', { position })}</p>
          <button
            type="button"
            className="mt-2 font-semibold"
            onClick={() => {
              onRetry(id);
            }}
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  const place: PlaceDetails = state.place;
  return (
    <SummaryCard
      place={place}
      port={port}
      position={position}
      selected={selected}
      onSelect={onSelect}
      wide={wide}
    />
  );
}

export function ConsultPlacesCard({ ui }: Readonly<GenUiRenderProps<ConsultPlacesBlock>>) {
  const { t } = useGenUiI18n();
  const {
    googleMapsApiKey,
    mapLoading,
    placePort,
    states,
    selectedId,
    focusedId,
    focusPlace,
    full,
    mapExpanded,
    setMapExpanded,
    loadSummary,
    loaded,
    anchorPlace,
    openDetails,
    close,
    sheetPlace,
  } = useConsultPlaces(ui);
  const railRef = useRef<HTMLUListElement>(null);
  useDockFocused(railRef, focusedId);
  const single = ui.placeIds.length === 1;
  return (
    <section
      aria-label={t('consultPlaces.aria')}
      className="genui2 space-y-4 rounded-3xl bg-card p-4 shadow-(--shadow-elevation)"
    >
      <PlacesHeader title={ui.title} />
      {googleMapsApiKey && loaded.length > 0 ? (
        <div className="relative">
          <PlacesGoogleMap
            apiKey={googleMapsApiKey}
            places={loaded}
            anchorPlace={anchorPlace}
            selectedId={focusedId ?? selectedId}
            onSelect={focusPlace}
            loadingLabel={t('consultPlaces.loadingMap')}
            errorLabel={t('consultPlaces.mapError')}
            retryLabel={t('consultPlaces.retryMap')}
            ariaLabel={t('consultPlaces.mapAria')}
          />
          <button
            type="button"
            className="absolute right-3 top-3 rounded-full bg-card/95 px-3 py-2 text-sm font-semibold shadow"
            onClick={() => {
              setMapExpanded(true);
            }}
          >
            {t('consultPlaces.expandMap')}
          </button>
          {anchorPlace && (
            <p className="mt-2 text-xs text-muted-foreground">
              <span aria-hidden>◆</span> {t('consultPlaces.referencePlace')}: {anchorPlace.name}
            </p>
          )}
        </div>
      ) : (
        <div className="grid min-h-36 place-items-center rounded-2xl bg-muted px-6 text-center text-sm text-muted-foreground">
          {t(mapLoading ? 'consultPlaces.loadingMap' : 'consultPlaces.mapUnavailable')}
        </div>
      )}
      <ul
        ref={railRef}
        aria-label={t('consultPlaces.list')}
        // `scroll-fade-x` (DS): dissolve a borda em que ainda há tiles — a pista de
        // que o trilho continua, já que o terceiro card sai cortado sem barra (#3249).
        className="scroll-fade-x page-bleed flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1"
      >
        {ui.placeIds.map((id, index) => (
          <li key={id} data-place-id={id} className={railItemClass(single)}>
            <PlaceTile
              id={id}
              state={states[id]}
              position={index + 1}
              port={placePort}
              selected={focusedId === id}
              onSelect={openDetails}
              onRetry={loadSummary}
              wide={single}
            />
          </li>
        ))}
      </ul>
      {sheetPlace && placePort && selectedId && (
        <PlaceSheet
          place={sheetPlace}
          port={placePort}
          onClose={close}
          loading={full?.status === 'loading'}
          failed={full?.status === 'error'}
          onRetry={() => {
            openDetails(selectedId);
          }}
        />
      )}
      {mapExpanded && googleMapsApiKey && loaded.length > 0 && (
        <MapDialog
          apiKey={googleMapsApiKey}
          places={loaded}
          anchorPlace={anchorPlace}
          selectedId={focusedId ?? selectedId}
          onClose={() => {
            setMapExpanded(false);
          }}
          onSelect={(placeId) => {
            setMapExpanded(false);
            focusPlace(placeId);
          }}
        />
      )}
    </section>
  );
}
